import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import type { CreditApplicationReference } from '@/lib/api-types'
import { formatDate } from '@/lib/format'
import {
  creditAppChipClasses,
  creditAppStatusLabel,
  referenceChipClasses,
  referenceStatusLabel,
} from '@/lib/status'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DescriptionList } from '@/components/ui/description-list'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

/**
 * The account's credit application: status summary + per-reference progress.
 * Trade references are emailed their form link automatically on submit; the
 * only action here is resending a link that went missing (Sent, not yet
 * Completed — the server 409s a completed one anyway).
 */
export function CreditPage() {
  const queryClient = useQueryClient()
  const [resendingId, setResendingId] = useState<string | null>(null)

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['credit-application'],
    queryFn: ({ signal }) => api.getCreditApplication({ signal }),
  })

  const resend = useMutation({
    mutationFn: (refId: string) => api.resendReference(refId),
    onMutate: (refId) => setResendingId(refId),
    onSettled: () => setResendingId(null),
    onSuccess: () => {
      toast.success('Reference email sent again.')
      void queryClient.invalidateQueries({ queryKey: ['credit-application'] })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'already_completed') {
        toast.error('That reference already came back — no resend needed.')
        void queryClient.invalidateQueries({ queryKey: ['credit-application'] })
        return
      }
      toast.error('The email didn’t send. Try again.')
    },
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading your credit application…" />
      </div>
    )
  }

  if (isError) {
    return (
      <ErrorState message="We couldn’t load your credit application." onRetry={() => void refetch()} />
    )
  }

  const application = data.application

  if (!application) {
    return (
      <div className="mx-auto max-w-form">
        <PageHeader title="Credit application" />
        <EmptyState
          className="mt-8"
          title="No credit application yet"
          action={
            <Button asChild>
              <Link to="/credit/apply">Start a credit application</Link>
            </Button>
          }
        >
          Set up credit terms with Green Line — the application takes about ten minutes.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-form">
      <PageHeader
        title="Credit application"
        description="Your application with Green Line’s credit team, and where each trade reference stands."
      />

      <Card className="mt-8">
        <DescriptionList
          columns={3}
          items={[
            { label: 'Application', value: application.name, mono: true },
            {
              label: 'Status',
              value: (
                <StatusChip
                  label={creditAppStatusLabel(application.status)}
                  chipClasses={creditAppChipClasses(application.status)}
                />
              ),
            },
            { label: 'Sent on', value: formatDate(application.submittedAt) },
          ]}
        />
      </Card>

      <section aria-labelledby="credit-references-heading" className="mt-10">
        <SectionHeader id="credit-references-heading" title="Trade references" />
        <p className="mt-2 text-sm text-mute">
          Each reference was emailed a secure form link automatically — nothing to chase unless one
          goes missing. Send it again if it does.
        </p>
        {application.references.length === 0 ? (
          <p className="mt-4 text-sm text-mute">No references on this application.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {application.references.map((reference) => (
              <ReferenceRow
                key={reference.id}
                reference={reference}
                resending={resendingId === reference.id}
                onResend={() => resend.mutate(reference.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function ReferenceRow({
  reference,
  resending,
  onResend,
}: {
  reference: CreditApplicationReference
  resending: boolean
  onResend: () => void
}) {
  const canResend = reference.status === 'Sent' && reference.completedAt === null
  return (
    <li>
      <Card padding="sm">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink" title={reference.referenceName}>
              {reference.referenceName}
            </p>
            <p className="mt-0.5 truncate font-mono text-xs text-mute" title={reference.email}>
              {reference.email}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-mute">
              {reference.completedAt
                ? `Completed ${formatDate(reference.completedAt)}`
                : reference.sentAt
                  ? `Sent ${formatDate(reference.sentAt)}`
                  : 'Not sent yet'}
            </span>
            <StatusChip
              label={referenceStatusLabel(reference.status)}
              chipClasses={referenceChipClasses(reference.status)}
            />
            {canResend ? (
              <Button variant="neutral" size="sm" loading={resending} onClick={onResend}>
                Send again
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
    </li>
  )
}
